import React, { useCallback } from 'react';
import { useResponsive } from '@sg/garnish';

import { Modal } from '@order/components';
import { useAddSweetpassPaymentMethod } from '@order/hooks';
import { BraintreeCreditCardForm } from '@order/integrations';
import { useLocalizationContext } from '@order/Localization';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassPaymentMethodAddModal = (
  props: SweetpassPaymentMethodAddModalProps,
) => {
  const { visible, onRequestClose } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  // ─── Mutation ────────────────────────────────────────────────────────

  const { isLoading: isSubmitting, addSweetpassPaymentMethod } =
    useAddSweetpassPaymentMethod();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnSave = useCallback(
    async (payload: CreditCardForm) => {
      return addSweetpassPaymentMethod({
        nonce: payload.id,
        onSuccess: onRequestClose,
      });
    },
    [addSweetpassPaymentMethod, onRequestClose],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={t('sweetpass.signed-in.add-payment-method-title')}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        {t('sweetpass.signed-in.add-payment-method-title')}
      </Modal.Header>

      <BraintreeCreditCardForm
        vault="subscription"
        excludedFields={['isDefault', 'nickname']}
        isSubmitting={isSubmitting}
        handleOnSave={handleOnSave}
      />
    </Modal>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardForm = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
}>;

type SweetpassPaymentMethodAddModalProps = Readonly<{
  visible: boolean;
  onRequestClose: () => void;
}>;
